import { baseApi } from "@/services/api";
import type {
  ApiCandidate,
  ApiEmployee,
  ApiEmployeeDetail,
  ApiEmployeeAuditEvent,
  AddEmployeeRequest,
  AddEmployeeResponse,
  EmployeeLifecycleReasonRequest,
  EmployeeMutationResponse,
  HardDeleteEmployeeRequest,
  HardDeleteEmployeeResponse,
  PurgeEmployeeRequest,
  PurgeEmployeeResponse,
} from "./employeeTypes";

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

interface ListEmployeesResponse {
  employees: ApiEmployee[];
}

interface ListCandidatesResponse {
  candidates: ApiCandidate[];
}

interface GetEmployeeResponse {
  employee: ApiEmployeeDetail;
}

interface GetEmployeeAuditResponse {
  employeeId: string;
  events: ApiEmployeeAuditEvent[];
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

function employeeSubPath(auth0Sub: string): string {
  return encodeURIComponent(auth0Sub);
}

export const employeesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listEmployees: builder.query<ListEmployeesResponse, void>({
      query: () => ({
        url: "/sa/employees",
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<ListEmployeesResponse>) =>
        unwrap(raw, "Failed to load employees"),
      providesTags: ["Employees"],
    }),
    getEmployee: builder.query<GetEmployeeResponse, string>({
      query: (employeeId) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<GetEmployeeResponse>) =>
        unwrap(raw, "Failed to load employee"),
      providesTags: (_result, _error, employeeId) => [
        { type: "Employees", id: employeeId },
      ],
    }),
    getEmployeeAudit: builder.query<GetEmployeeAuditResponse, string>({
      query: (employeeId) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}/audit`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<GetEmployeeAuditResponse>) =>
        unwrap(raw, "Failed to load employee audit history"),
      providesTags: (_result, _error, employeeId) => [
        { type: "Employees", id: `${employeeId}-audit` },
      ],
    }),
    listEmployeeCandidates: builder.query<ListCandidatesResponse, void>({
      query: () => ({
        url: "/sa/employees/candidates",
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<ListCandidatesResponse>) =>
        unwrap(raw, "Failed to load pending employees"),
      providesTags: ["Employees"],
    }),
    enrollEmployee: builder.mutation<AddEmployeeResponse, AddEmployeeRequest>({
      query: (body) => ({
        url: "/sa/employees",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<AddEmployeeResponse>) =>
        unwrap(raw, "Failed to add employee"),
      invalidatesTags: ["Employees"],
    }),
    activateEmployee: builder.mutation<EmployeeMutationResponse, string>({
      query: (auth0Sub) => ({
        url: `/sa/employees/${employeeSubPath(auth0Sub)}/activate`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<EmployeeMutationResponse>) =>
        unwrap(raw, "Failed to activate employee"),
      invalidatesTags: ["Employees"],
    }),
    suspendEmployee: builder.mutation<
      EmployeeMutationResponse,
      EmployeeLifecycleReasonRequest
    >({
      query: ({ auth0Sub, reason }) => ({
        url: `/sa/employees/${employeeSubPath(auth0Sub)}/suspend`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: (raw: ApiEnvelope<EmployeeMutationResponse>) =>
        unwrap(raw, "Failed to suspend employee"),
      invalidatesTags: ["Employees"],
    }),
    restoreEmployee: builder.mutation<
      EmployeeMutationResponse,
      EmployeeLifecycleReasonRequest
    >({
      query: ({ auth0Sub, reason }) => ({
        url: `/sa/employees/${employeeSubPath(auth0Sub)}/restore`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: (raw: ApiEnvelope<EmployeeMutationResponse>) =>
        unwrap(raw, "Failed to restore employee"),
      invalidatesTags: ["Employees"],
    }),
    deleteEmployee: builder.mutation<
      HardDeleteEmployeeResponse,
      HardDeleteEmployeeRequest
    >({
      query: ({ employeeId, confirmPhrase, confirmEmployeeId, reason }) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}/delete`,
        method: "POST",
        body: { confirmPhrase, confirmEmployeeId, reason },
      }),
      transformResponse: (raw: ApiEnvelope<HardDeleteEmployeeResponse>) =>
        unwrap(raw, "Failed to delete employee"),
      invalidatesTags: ["Employees"],
    }),
    purgeEmployee: builder.mutation<PurgeEmployeeResponse, PurgeEmployeeRequest>({
      query: ({ employeeId, confirmPhrase, confirmEmployeeId, reason }) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}/purge`,
        method: "POST",
        body: { confirmPhrase, confirmEmployeeId, reason },
      }),
      transformResponse: (raw: ApiEnvelope<PurgeEmployeeResponse>) =>
        unwrap(raw, "Failed to purge employee"),
      invalidatesTags: ["Employees"],
    }),
  }),
});

export const {
  useListEmployeesQuery,
  useGetEmployeeQuery,
  useGetEmployeeAuditQuery,
  useListEmployeeCandidatesQuery,
  useEnrollEmployeeMutation,
  useActivateEmployeeMutation,
  useSuspendEmployeeMutation,
  useRestoreEmployeeMutation,
  useDeleteEmployeeMutation,
  usePurgeEmployeeMutation,
} = employeesApi;
